import React from 'react';
import {
  getAvatarBackgroundStyle,
  getItemPicAsset,
  getPicEntryById,
} from '../../utils/item';
import { renderIconBySlug } from '../../content/icons';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { TextMd } from '../text/text.Md';

export const FacePreview = ({
  face,
  ownerItem,
  size = 16,
  className = '',
}) => {
  const type = face?.type || 'text';
  if (type === 'icon' && face?.iconSlug) {
    return (
      <span className={`text-contrast text-opacity-90 ${className}`}>
        {renderIconBySlug(face.iconSlug, {
          size,
          color: 'var(--contrast)',
        })}
      </span>
    );
  }
  if (type === 'pic' && face?.picId) {
    const entry = getPicEntryById(ownerItem?.pics || [], face.picId);
    const url = entry?.url || null;
    if (!url) {
      return <span className='text-contrast text-opacity-70'>?</span>;
    }
    const px = Math.max(12, Math.round(size + 2));
    return (
      <span
        className='rounded border border-primary border-opacity-20 overflow-hidden bg-main bg-opacity-30'
        style={{
          width: px,
          height: px,
          display: 'inline-block',
          backgroundImage: `url(${url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }
  // text
  return (
    <span className={`text-contrast text-opacity-90 ${className}`}>
      {typeof face?.text === 'string' && face.text.trim() !== ''
        ? face.text
        : ''}
    </span>
  );
};

export const SourceAvatar = ({ item, tooltip = null }) => {
  const asset = getItemPicAsset(item, 0);
  const bgStyle = asset?.bgColor ? getAvatarBackgroundStyle(asset.bgColor) : {};
  const hasBorderColor =
    asset?.bgColor && asset.bgColor !== 'transparent' ? true : false;
  const borderColor = hasBorderColor
    ? `rgb(var(--${asset.bgColor}))`
    : 'rgba(255,255,255,0.12)';
  const url = asset?.url || null;
  const iconSlug = asset?.iconSlug || null;

  const node = (
    <div
      className='rounded-full shadow bg-main bg-opacity-50 flex items-center justify-center overflow-hidden'
      style={{
        width: 30,
        height: 30,
        border: `2px solid ${borderColor}`,
        ...(iconSlug || url ? bgStyle : {}),
        backgroundImage: url && !iconSlug ? `url(${url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {iconSlug
        ? renderIconBySlug(iconSlug, {
            size: 14,
            color: 'var(--contrast)',
          })
        : null}
    </div>
  );
  if (tooltip) {
    return <TooltipHtml title={tooltip}>{node}</TooltipHtml>;
  }
  return node;
};

export const DiceResultBadge = ({
  label,
  iconSlug = null,
  color = null,
  description = null,
  itemId = null,
  textId = null,
  className = '',
}) => {
  if (!label) return null;
  const node = (
    <div
      className={`text-xs uppercase tracking-wide px-02 py-01 rounded border text-contrast text-opacity-70 whitespace-nowrap flex items-center gap-02 ${className}`}
      style={
        color && color !== 'transparent'
          ? {
              backgroundColor: `rgb(var(--${color}) / 0.5)`,
              borderColor: `rgb(var(--${color}) / 0.6)`,
            }
          : {
              backgroundColor: 'rgba(0,0,0,0.22)',
              borderColor: 'rgba(255,255,255,0.10)',
            }
      }
    >
      {iconSlug
        ? renderIconBySlug(iconSlug, { size: 12, color: 'var(--contrast)' })
        : null}
      <span className='text-xs'>{label}</span>
    </div>
  );

  const tip = typeof description === 'string' ? description.trim() : '';
  if (tip) {
    return (
      <TooltipHtml
        title={
          <div className='max-w-xs break-words text-sm leading-normal'>
            <TextMd
              str={tip}
              textId={textId || `dice-result-${label || 'result'}`}
              itemId={itemId}
            />
          </div>
        }
      >
        {node}
      </TooltipHtml>
    );
  }
  return node;
};

export const renderRollToken = (
  value,
  face,
  ownerItem,
  key,
  { faceSize = 16, textClassName = '' } = {},
) => {
  if (face) {
    return (
      <span key={key}>
        <FacePreview
          face={face}
          ownerItem={ownerItem}
          size={faceSize}
          className={textClassName}
        />
      </span>
    );
  }
  return (
    <span
      key={key}
      className={`text-contrast text-opacity-90 ${textClassName}`}
    >
      {value}
    </span>
  );
};
